import { nextTick, ref } from 'vue'

// Keep in sync with the .row-highlight animation duration in styles/style.scss
export const HIGHLIGHT_DURATION_MS = 1500

// Resolves when the smooth scroll settles: on the first `scrollend`, or right away
// if nothing scrolls, or after a hard cap for browsers without `scrollend`.
const waitForScrollSettleAsync = () => new Promise((resolve) => {
  let scrolled = false
  let done = false
  const finish = () => {
    if (done) return
    done = true
    clearTimeout(noScrollTimer)
    clearTimeout(hardCap)
    document.removeEventListener('scroll', onScroll, true)
    document.removeEventListener('scrollend', finish, true)
    resolve()
  }
  const onScroll = () => { scrolled = true }
  const noScrollTimer = setTimeout(() => { if (!scrolled) finish() }, 120)
  const hardCap = setTimeout(finish, 1000)
  document.addEventListener('scroll', onScroll, true)
  document.addEventListener('scrollend', finish, true)
})

export function useHighlightNewRow() {
  const highlightedRowId = ref(null)
  let timer = null

  const highlightRowAsync = async(rowId, container) => {
    clearTimeout(timer)
    if (rowId == null) {
      highlightedRowId.value = null
      return
    }
    const root = container?.value?.$el ?? container?.value ?? container
    root?.classList?.add('highlight-scrolling')
    highlightedRowId.value = rowId
    await nextTick()
    const el = root?.querySelector?.('.row-highlight')
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
    await waitForScrollSettleAsync()
    root?.classList?.remove('highlight-scrolling')
    timer = setTimeout(() => { highlightedRowId.value = null }, HIGHLIGHT_DURATION_MS)
  }

  return { highlightedRowId, highlightRowAsync }
}

const MIDDLE_MOUSE_BUTTON = 1

export function useMiddleButtonPan(containerRef) {
  let startX = 0
  let startScrollLeft = 0

  const onMouseMove = (event) => {
    containerRef.value.scrollLeft = startScrollLeft - (event.clientX - startX)
  }

  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
    containerRef.value?.classList.remove('cursor-grabbing')
  }

  const onMouseDown = (event) => {
    if (event.button !== MIDDLE_MOUSE_BUTTON || !containerRef.value) return
    event.preventDefault()
    startX = event.clientX
    startScrollLeft = containerRef.value.scrollLeft
    containerRef.value.classList.add('cursor-grabbing')
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  return { onMouseDown }
}
